import { beaconActivityEvents, postActivityEvents } from 'services/activityTracking/activityEventService';
import {
    ACTIVITY_COALESCE_WINDOW_MS,
    ACTIVITY_DROP_STATUSES,
    ACTIVITY_MAX_BATCH_BYTES,
    ACTIVITY_MAX_BEACON_BYTES,
    ACTIVITY_MAX_EVENTS_PER_REQUEST,
    ACTIVITY_MAX_EVENT_BYTES,
    ACTIVITY_RETRY_DELAYS_MS,
    ACTIVITY_RETRY_JITTER_RATIO,
    ACTIVITY_RETRYABLE_STATUSES,
    ACTIVITY_STOP_STATUSES,
} from 'utils/activityTracking/constants';

/**
 * The activity event queue (BEAST-13036).
 *
 * THE QUEUE IS DUMB, THE STATE MACHINE IS SMART. This module batches, caps, splits, retries and
 * flushes. It does not know what a focus period is, and it must never merge, reorder, coalesce or
 * drop duplicates: returning to a transaction opens a genuinely new period, and any
 * "collapse identical events" optimization here would destroy real data. Events go out in the order
 * they were created, which is also `producer_sequence` order.
 *
 * Flush policy, verbatim from the architecture page: a focus end adds the event to the queue and
 * does NOT create a request per blur. Events coalesce for no longer than one second. The queue
 * flushes earlier when the batch limit is reached, and immediately on transaction completion,
 * document hidden, page unload, logout and desk leave. There is one coalescing timer and one flush
 * path, and every immediate trigger CANCELS the pending timer rather than racing it.
 */

let pending = [];
let coalesceTimer = null;

/** Feature flag state. Default off, so an unconfigured queue makes zero requests. */
let enabled = false;

/**
 * Set by a 401 or 403 and never cleared: both are permanent, and the queue stops for the lifetime of
 * the page. No closing events are synthesized afterwards - the architecture page is explicit that
 * the browser does not fabricate an event after server-side expiry without a usable signal, and an
 * open period is the correct output there.
 */
let stopped = false;

function byteLength(value) {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value).length;
    return unescape(encodeURIComponent(value)).length;
}

function serializedByteLength(event) {
    try {
        return byteLength(JSON.stringify(event));
    } catch {
        // An event that cannot even be serialized is treated as oversized, so it is dropped with a
        // log rather than taking a whole batch down at send time.
        return Number.POSITIVE_INFINITY;
    }
}

/**
 * Splits a backlog into payloads that satisfy both caps. An oversized backlog is ALWAYS split,
 * never dropped. A single event that cannot fit on its own is the one exception - it can only be
 * dropped, and at a few hundred bytes per event that is a defensive branch, not a real path.
 */
export function chunkActivityEvents(events, maxEvents = ACTIVITY_MAX_EVENTS_PER_REQUEST, maxBytes = ACTIVITY_MAX_BATCH_BYTES) {
    const chunks = [];
    let current = [];
    let currentBytes = 0;

    events.forEach((event) => {
        const size = serializedByteLength(event);

        // Per-event cap, ours to enforce: an event over it would come back as that one event in
        // `rejected[]`, so there is nothing to gain by sending it.
        if (size > ACTIVITY_MAX_EVENT_BYTES) {
            console.warn(`activityTracking: dropping event ${event?.event_id} at ${size} bytes, over the ${ACTIVITY_MAX_EVENT_BYTES} byte cap.`);
            return;
        }

        if (current.length && (current.length >= maxEvents || currentBytes + size > maxBytes)) {
            chunks.push(current);
            current = [];
            currentBytes = 0;
        }

        current.push(event);
        currentBytes += size;
    });

    if (current.length) chunks.push(current);
    return chunks;
}

function retryDelayMs(attempt, retryAfterMs) {
    if (Number.isFinite(retryAfterMs) && retryAfterMs > 0) return retryAfterMs;
    const base = ACTIVITY_RETRY_DELAYS_MS[attempt] ?? ACTIVITY_RETRY_DELAYS_MS[ACTIVITY_RETRY_DELAYS_MS.length - 1];
    const jitter = base * ACTIVITY_RETRY_JITTER_RATIO * (Math.random() * 2 - 1);
    return Math.max(0, Math.round(base + jitter));
}

function logRejected(rejected) {
    if (!rejected?.length) return;
    // Any event in `rejected[]` is a bug in our payload: log it once, drop it, never retry it.
    rejected.forEach(({ event_id: eventId, reason }) => {
        console.warn(`activityTracking: event ${eventId} rejected by the backend: ${reason}`);
    });
}

/**
 * Sends one chunk, retrying only what is worth retrying. Never throws: a rejected promise here
 * would surface as an unhandled rejection on a path the agent is standing in.
 */
async function sendChunk(chunk, attempt = 0) {
    if (stopped) return;

    const { ok, status, rejected, networkError, retryAfterMs } = await postActivityEvents(chunk);

    if (ok) {
        logRejected(rejected);
        return;
    }

    if (status !== null && ACTIVITY_STOP_STATUSES.includes(status)) {
        stopped = true;
        pending = [];
        console.warn(`activityTracking: ${status} from the activity endpoint; the queue is stopped for the lifetime of this page.`);
        return;
    }

    if (status !== null && ACTIVITY_DROP_STATUSES.includes(status)) {
        console.warn(`activityTracking: ${status} from the activity endpoint; dropping ${chunk.length} event(s). This is a payload bug on our side.`);
        return;
    }

    const isTransient = networkError || (status !== null && ACTIVITY_RETRYABLE_STATUSES.includes(status));
    if (!isTransient) {
        console.warn(`activityTracking: unexpected status ${status} from the activity endpoint; dropping ${chunk.length} event(s).`);
        return;
    }

    // `>= length`, not `>= length - 1`: with the off-by-one the third delay was unreachable, so
    // `20000` was dead and the bound was two retries rather than the three the contract names.
    if (attempt >= ACTIVITY_RETRY_DELAYS_MS.length) {
        console.warn(`activityTracking: giving up on ${chunk.length} event(s) after ${attempt + 1} attempts.`);
        return;
    }

    // The chunk is retried UNCHANGED, so every `event_id` stays byte-identical across attempts.
    // That is the only reason at-least-once delivery is safe: the shared service deduplicates on it.
    const delay = retryDelayMs(attempt, retryAfterMs);
    setTimeout(() => {
        sendChunk(chunk, attempt + 1);
    }, delay);
}

function cancelCoalesceTimer() {
    if (coalesceTimer !== null) {
        clearTimeout(coalesceTimer);
        coalesceTimer = null;
    }
}

function scheduleCoalesceFlush() {
    if (coalesceTimer !== null) return;
    coalesceTimer = setTimeout(() => {
        coalesceTimer = null;
        flushActivityQueue();
    }, ACTIVITY_COALESCE_WINDOW_MS);
}

/** Turns the whole transport on or off. With it off the queue holds nothing and sends nothing. */
export function configureActivityQueue({ enabled: isEnabled }) {
    enabled = !!isEnabled;
    if (!enabled) {
        cancelCoalesceTimer();
        pending = [];
    }
}

export function isActivityQueueEnabled() {
    return enabled && !stopped;
}

/**
 * Adds one already-built envelope to the queue. Reaching the batch limit flushes before the timer
 * expires; otherwise the coalescing timer is armed so eleven blurs in a row become one request.
 */
export function enqueueActivityEvent(event) {
    try {
        if (!enabled || stopped || !event) return;

        pending.push(event);

        if (pending.length >= ACTIVITY_MAX_EVENTS_PER_REQUEST) {
            flushActivityQueue();
            return;
        }

        scheduleCoalesceFlush();
    } catch (error) {
        console.warn('activityTracking: failed to enqueue an activity event.', error);
    }
}

/**
 * The single flush path. Always cancels the pending coalescing timer first, so an immediate trigger
 * can never race it into a duplicate request.
 *
 * @param {boolean} [useBeacon] for the triggers where a normal XHR may not survive the page:
 *   document hidden, page unload, logout and desk leave.
 */
export function flushActivityQueue({ useBeacon = false } = {}) {
    try {
        cancelCoalesceTimer();

        if (!enabled || stopped || !pending.length) return;

        const batch = pending;
        pending = [];

        if (useBeacon) {
            // The beacon has its own ceiling far below the batch cap, so the backlog is split
            // against that limit instead and each body is checked on its own.
            chunkActivityEvents(batch, ACTIVITY_MAX_EVENTS_PER_REQUEST, ACTIVITY_MAX_BEACON_BYTES).forEach((chunk) => {
                beaconActivityEvents(chunk);
            });
            return;
        }

        chunkActivityEvents(batch).forEach((chunk) => {
            sendChunk(chunk);
        });
    } catch (error) {
        console.warn('activityTracking: failed to flush the activity queue.', error);
    }
}

/** Test seam only: every value above is module state that would otherwise leak between tests. */
export function resetActivityQueue() {
    cancelCoalesceTimer();
    pending = [];
    enabled = false;
    stopped = false;
}

/** Test seam only. */
export function getPendingActivityEventCount() {
    return pending.length;
}
